import { GRANOS_CIERRE_DEFAULT } from '../shared/models/constants';
import type { CierreGrano } from '../shared/types/autorizacion';
import type {
  AllocateKilosResult,
  GrainCampaign,
  GrainCosechaEntry,
  GrainGroup,
} from '../shared/types/grain';
import { resolvePrecioArsForDisplay } from '../shared/utils/precio-cierre';

export function grainKeyFromCampaignName(name: string): string {
  return ((name || '').trim().split(/\s+/)[0] || '').toUpperCase();
}

/** Posse a veces devuelve saldo en total con available en 0. */
export function resolveCampaignDisponibleKg(campaign: GrainCampaign): number {
  const available = Number(campaign.balance?.available ?? 0);
  if (Number.isFinite(available) && available > 0) return available;
  const total = Number(campaign.balance?.total ?? 0);
  if (Number.isFinite(total) && total > 0) return total;
  return 0;
}

/** Resta solicitudes pendientes de la disponibilidad por cosecha (FIFO). */
export function applyPendingReservationsToCampaigns(
  campaigns: GrainCampaign[],
  pendingByGrano: Record<string, number>
): GrainCampaign[] {
  if (!campaigns.length || !Object.keys(pendingByGrano).length) return campaigns;

  const remaining = { ...pendingByGrano };
  return campaigns.map((campaign) => {
    const grain = grainKeyFromCampaignName(campaign.name);
    const reserve = remaining[grain] ?? 0;
    if (reserve <= 0) return campaign;

    const available = resolveCampaignDisponibleKg(campaign);
    if (available <= 0) return campaign;

    const subtract = Math.min(available, reserve);
    remaining[grain] = reserve - subtract;

    return {
      ...campaign,
      balance: {
        ...campaign.balance,
        available: available - subtract,
        total: campaign.balance?.total ?? 0,
      },
    };
  });
}

/** Una campaña del productor → grupo con una sola cosecha (alta manual sin saldo neto). */
export function buildGrainGroupFromCampaign(
  campaign: GrainCampaign,
  pendingByGrano: Record<string, number> = {}
): GrainGroup {
  const grain = grainKeyFromCampaignName(campaign.name);
  const available = resolveCampaignDisponibleKg(campaign);
  const total = Number(campaign.balance?.total ?? 0);
  return {
    grain,
    availableKg: available,
    balanceTotal: Number.isFinite(total) ? total : 0,
    pendingReservedKg: pendingByGrano[grain] ?? 0,
    cosechas: [
      {
        id: campaign.id,
        name: campaign.name,
        balance: {
          ...(campaign.balance ?? {}),
          available,
          total: Number.isFinite(total) ? total : available,
        },
      },
    ],
  };
}

/** Grano con cosechas reales a descontar (excluye filas solo por cierre mercado). */
export function hasAssignableGrainAvailability(groups: GrainGroup[]): boolean {
  return groups.some((g) => g.availableKg > 0 && g.cosechas.length > 0);
}

export function buildGrainGroups(
  campaigns: GrainCampaign[],
  pendingByGrano: Record<string, number> = {}
): GrainGroup[] {
  const groups: Record<string, GrainGroup> = {};

  campaigns.forEach((c) => {
    const available = resolveCampaignDisponibleKg(c);
    const total = c.balance?.total ?? 0;
    const grain = ((c.name || '').trim().split(/\s+/)[0] || '').toUpperCase();
    if (!grain) return;

    if (!groups[grain]) {
      groups[grain] = { grain, availableKg: 0, balanceTotal: 0, cosechas: [] };
    }

    if (available > 0) {
      groups[grain]!.cosechas.push({
        id: c.id,
        name: c.name,
        balance: c.balance ?? {},
      });
      groups[grain]!.availableKg += available;
    }
    groups[grain]!.balanceTotal += total;
  });

  const result: GrainGroup[] = [];
  GRANOS_CIERRE_DEFAULT.forEach((g) => {
    const group = groups[g];
    if (group && group.availableKg > 0) {
      group.pendingReservedKg = pendingByGrano[g] ?? 0;
      result.push(group);
      delete groups[g];
    }
  });
  Object.keys(groups).forEach((g) => {
    const group = groups[g];
    if (group && group.availableKg > 0) {
      group.pendingReservedKg = pendingByGrano[g] ?? 0;
      result.push(group);
    }
  });
  return result;
}

/** Granos con precio de cierre válido para la fecha (ARS / qq). */
export function granosConCierreMercado(
  cierres: CierreGrano[],
  tipoCambio: number | null
): string[] {
  const out: string[] = [];
  for (const c of cierres) {
    const grano = String(c.grano || '').trim().toUpperCase();
    if (!grano) continue;
    const ars = resolvePrecioArsForDisplay(
      Number(c.precio),
      c.precioArs,
      c.moneda,
      tipoCambio
    );
    if (ars != null && ars > 0) out.push(grano);
  }
  return out;
}

/**
 * Alta admin: incluir cereales con cierre de mercado aunque no haya saldo neto
 * (p. ej. todo reservado en pendientes o canjes).
 */
export function mergeGrainGroupsWithMercadoCierre(
  groups: GrainGroup[],
  cierreGranos: string[]
): GrainGroup[] {
  if (!cierreGranos.length) return groups;
  const byGrain = new Map(groups.map((g) => [g.grain, g]));
  const result: GrainGroup[] = [];
  const seen = new Set<string>();

  GRANOS_CIERRE_DEFAULT.forEach((g) => {
    const existing = byGrain.get(g);
    if (existing) {
      result.push(existing);
      seen.add(g);
    } else if (cierreGranos.includes(g)) {
      result.push({
        grain: g,
        availableKg: 0,
        balanceTotal: 0,
        cosechas: [],
        pendingReservedKg: 0,
      });
      seen.add(g);
    }
  });

  groups.forEach((g) => {
    if (!seen.has(g.grain)) result.push(g);
  });
  return result;
}

export interface AllocateKilosOptions {
  /** Asigna el excedente en la última cosecha (solo operadores del panel). */
  allowOverflow?: boolean;
}

export function allocateKilosAcrossCosechas(
  cosechas: GrainCosechaEntry[],
  kilos: number,
  options?: AllocateKilosOptions
): AllocateKilosResult {
  let remaining = kilos;
  const allocations: AllocateKilosResult['allocations'] = [];

  cosechas.forEach((c) => {
    if (remaining <= 0) return;
    const available = resolveCampaignDisponibleKg({
      id: c.id,
      name: c.name,
      balance: c.balance,
    });
    if (available <= 0) return;
    const take = Math.min(remaining, available);
    allocations.push({
      idCosecha: c.id,
      cosecha: c.name,
      kilos: take,
      disponibleKg: available,
      balance: c.balance,
    });
    remaining -= take;
  });

  if (remaining > 0 && options?.allowOverflow && cosechas.length > 0) {
    const lastAlloc = allocations[allocations.length - 1];
    if (lastAlloc) {
      lastAlloc.kilos += remaining;
    } else {
      const c = cosechas[cosechas.length - 1]!;
      const disponibleKg = resolveCampaignDisponibleKg({
        id: c.id,
        name: c.name,
        balance: c.balance,
      });
      allocations.push({
        idCosecha: c.id,
        cosecha: c.name,
        kilos: remaining,
        disponibleKg,
        balance: c.balance,
      });
    }
    remaining = 0;
  }

  return { allocations, kilosSinAsignar: remaining };
}
